/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { GenericIconEnum } from '../../atoms/index.js';
import { GenericIconSize } from '../../atoms/generic-icon/nn-generic-icon.enum';

/**
 * Default Modal used in Odil.
 *
 * @prop title The title of the modal.
 * @prop id Must be unique (auto-generated).
 * @slot openButton The button to open the modal.
 * @slot content The content inside the modal.
 * @slot footer The footer inside the modal.
 */
@customElement('nn-modal')
export class Modal extends LitElement {
  static styles = css`
    .nn-modal {
      display: none;
      position: fixed;
      z-index: 2000;
      justify-content: center;
      align-items: center;
      text-align: center;
      left: 0;
      top: 0;
      width: 100%;
      height: calc(100% - 60px);
      background: rgba(0, 0, 0, 0.15);
      backdrop-filter: blur(4px);
      font-family: font-light;
      cursor: default;
      padding: 30px 0;
      overflow: auto;
    }

    .nn-modal__container {
      border-radius: 32px;
      background-color: #ffffff;
      margin: auto;
      border: 1px solid #888;
      box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2),
        0px 2px 2px rgba(0, 0, 0, 0.12), 0px 0px 2px rgba(0, 0, 0, 0.14);
      max-width: 60%;
      display: flex;
      flex-direction: column;
    }

    .nn-modal__content--close {
      cursor: pointer;
      float: right;
      text-align: right;
      padding: 32px 50px 0 0;
    }

    .nn-modal__title {
      font-family: 'font-heavy';
      font-size: 40px;
      font-weight: 500;
      text-align: left;
      margin-top: 0px;
      padding: 0 50px;
    }

    .nn-modal__content {
      text-align: left;
      margin: 0 0 16px 0;
      padding: 0 50px;
    }

    .nn-modal--open {
      display: flex;
    }

    .nn-modal__footer {
      background: #f0f0f0;
      border-radius: 0 0 30px 30px;
      border: 0;
    }

    @media (max-width: 991px) {
      .nn-modal__container {
        max-width: 80%;
      }
    }
    @media (max-width: 767px) {
      .nn-modal {
        height: 100%;
        padding: 0;
      }

      .nn-modal__container {
        border-radius: 0;
        border: none;
        max-width: 100%;
        min-height: 100%;
      }

      .nn-modal__content {
        padding: 0 10px;
      }

      .nn-modal__content--close {
        padding-right: 12px;
        padding-top: 12px;
      }

      .nn-modal__title {
        padding: 0px 10px;
        margin-bottom: 20px;
        line-height: 3rem;
      }
    }
  `;

  @property({ type: String }) title = '';

  @property({ type: String }) id = (Math.random() + 1)
    .toString(36)
    .substring(7);

  @property({ type: Boolean }) openState = false;

  @property({ type: Boolean }) disabled = false;

  constructor() {
    super();
    this.addEventListener('click', (e: MouseEvent) => {
      if (undefined !== (e.target as HTMLElement).dataset.closeModal) {
        this._dispatchClose(e);
      }
    });
  }

  renderTitle() {
    if (this.title) {
      return html`<p class="nn-modal__title">${this.title}</p>`;
    }
    return '';
  }

  render() {
    return html`
      <slot name="openButton" @click="${this._dispatchOpen}"></slot>
      <div
        id="nn-modal-${this.id}"
        class="nn-modal ${this.openState
          ? 'nn-modal--open'
          : 'nn-modal--close'}"
        @click="${this.closeBackdrop}"
      >
        <div class="nn-modal__container" data-close-modal>
          <nn-generic-icon
            iconName="${GenericIconEnum.CLOSE}"
            size="${GenericIconSize.MEDIUM}"
            class="nn-modal__content--close"
            @click="${this._dispatchClose}"
          ></nn-generic-icon>
          ${this.renderTitle()}
          <div class="nn-modal__content">
            <slot name="content"></slot>
          </div>
          <div class="nn-modal__footer">
            <slot class="nn-modal__footer" name="footer"></slot>
          </div>
        </div>
      </div>
    `;
  }

  _dispatchOpen(e: Event) {
    if (this.disabled) return;
    const htmlPage = document.querySelector('body');
    if (htmlPage) {
      htmlPage.style.overflow = 'hidden';
    }
    this.openState = true;
    e.preventDefault();
    const event = new CustomEvent('onOpen');
    this.dispatchEvent(event);
  }

  _dispatchClose(e: Event) {
    const htmlPage = document.querySelector('body');
    if (htmlPage) {
      htmlPage.removeAttribute('style');
    }
    this.openState = false;
    e.preventDefault();
    const event = new CustomEvent('onClose');
    this.dispatchEvent(event);
  }

  closeBackdrop(e: Event) {
    const el = e.target as HTMLElement;
    if (el.classList.contains('nn-modal--open')) {
      this._dispatchClose(e);
    }
  }
}
